/* eslint-disable */
import type { AudienceOption } from '@src/components/common/AudienceSelector';
import type { GoalOption } from '@src/components/common/GoalSelector';
import type { Speaker } from '@src/components/content-components/SpeakerTypes';
import type { AIPromptRepository } from '@src/repositories';
import type { TranscriptionStore } from '@src/stores-v2';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import { logChatGptOutput } from '@src/utility/datadog';
import axios from 'axios';
import TurndownService from 'turndown';
import { ORIGINAL_VIDEO_LANGUAGE_OPTIONS } from '@src/config/languages';

import type { AIModel, AIPrompt } from '../types.ts/ai_prompts';
import type { TalkingPointContent } from '../types.ts/general';
import type {
  ContentViewData,
  PunchListItem,
  SupportedGeneratedContentTypes,
} from '../types.ts/story';
import type { FrameAnalysisResult } from '../utility/frameExtraction';
import { AIEventTracker } from '@src/monitoring/utils/AIEventTracker';
import { AIPromptLogger } from '@src/monitoring/utils/AIPromptLogger';
import { LogManager } from '@src/utility/Logger';
import {
  buildOverlappingChunks,
  buildRosterTextForAnchors,
  extractSpeakerSegments,
} from '../utility/speakerIdentification';
import { buildAdjustedCountInstruction } from '../utility/revisionLengthParser';
import {
  type ResolvedModel,
  resolveAIModel,
  spreadModelParams,
} from '../repositories/resolveAIModel';
import { readSpeakerNameFromCache } from '@src/stores-v2/speakerCache';
import { stripHtml } from '@src/utility/sanitizeHtml';
import { extractErrorInfo } from '@src/utility/error';

const turndownService = new TurndownService();

type GenerateStreamResponseParams = {
  key: SupportedGeneratedContentTypes;
  template?: AIPrompt;
  version?: '1' | '2';
  type?: 'existing' | 'new' | 'regenerate';
  customInstructions?: string;
  revision?: string;
  brandVoice?: string;
  avoidWords?: string;
  // For speaker filtering
  selectedSpeakers?: Speaker[];
  // Whether the user explicitly toggled the speaker filter (vs speakers auto-included for attribution)
  speakerFilterActive?: boolean;
  // For goal targeting
  selectedGoal?: GoalOption | null;
  // For audience targeting
  selectedAudiences?: AudienceOption[];
  selectedLanguage?: string | null;
  model?: string;
  aiModel?: AIModel | null;
  // Brand Kit audience/goal generation doesn't require a story
  skipStoryValidation?: boolean;
};

export default class ChatGPTService {
  protected videoCreator: IVideoCreatorStore;

  protected transcriptionStore: TranscriptionStore;

  protected aiPromptRepository: AIPromptRepository;

  public streamProcessDone = false;

  private controller: AbortController | null = null;

  public talkingPointController: AbortController | null = null;

  private logger = LogManager.getLogger('service:ChatGPTService');

  constructor(
    videoCreator: IVideoCreatorStore,
    aiPromptRepository: AIPromptRepository,
  ) {
    this.videoCreator = videoCreator;
    this.transcriptionStore = videoCreator.rootStore.transcriptionStore;
    this.aiPromptRepository = aiPromptRepository;
  }

  /** Build common context for AIPromptLogger entries */
  protected getPromptLogContext() {
    const identity =
      this.videoCreator.rootStore.userIdentityStore.currentIdentity;
    return {
      personId: this.videoCreator.rootStore.datoPersonStore.person?.id,
      storyId: this.videoCreator.story?.id,
      showcaseId: this.videoCreator.story?.primaryShowcase?.id,
      metadata: {
        user_email: identity?.email,
        user_id: identity?.id,
        dato_environment:
          this.videoCreator.rootStore.userIdentityStore.environment,
      },
    };
  }

  /** Get the DatoCMS client for fire-and-forget log saves */
  protected get datoClient() {
    return this.videoCreator.rootStore.datoClientStore.datoClient;
  }

  fetchAiPrompt = async (key: string) => {
    const prompt = await this.aiPromptRepository.getPromptByTitle(key);
    return prompt;
  };

  /**
   * PLAT-9028: Templates loaded via customer-type queries use the base GQL
   * fragment which omits model, internalTitle, and promptType. Re-fetch
   * the prompt to pick up those fields when they're missing.
   *
   * Re-fetch by record id, never by title: prompt titles are not unique across
   * customer types (several own an "Instagram Post"), so a title lookup swaps
   * in whichever record DatoCMS returns first — serving one customer's prompt
   * to another. Enrichment may add fields, never change which record you got.
   *
   * Merge only the three missing fields rather than replacing the object: the
   * extended fragment omits userPromptDescription, which the base fragment
   * carries, so a wholesale swap would drop it.
   */
  private enrichPromptIfNeeded = async (
    prompt: AIPrompt,
  ): Promise<AIPrompt> => {
    if (!prompt.model && !prompt.internalTitle) {
      const enriched = await this.aiPromptRepository.getPromptById(prompt.id);
      if (enriched) {
        return {
          ...prompt,
          model: enriched.model,
          internalTitle: enriched.internalTitle,
          promptType: enriched.promptType,
        };
      }
    }
    return prompt;
  };

  private getExistingResponseData = (key: SupportedGeneratedContentTypes) =>
    this.videoCreator.contentStudioGeneratedContent?.[key]?.content?.response ||
    '';

  public getTranscript = (
    key: SupportedGeneratedContentTypes,
    selectedSpeakers?: Speaker[],
  ): string | undefined => {
    try {
      let elements;

      // Get the right elements based on content type
      if (key === 'Music Selection' || key === 'Photo Punchlist') {
        elements = this.transcriptionStore
          .getFinalTranscriptionElements()
          ?.filter((el: any) => el.state !== 'removed' && el.state !== 'cut');
      } else {
        elements = this.transcriptionStore.originalTranscription?.elements;
      }

      // Safety check - if no elements, return early
      if (!elements || elements.length === 0) {
        this.logger.warn('No transcript elements found for key:', key);
        return '';
      }
      // Apply speaker filtering if specified.
      // Skip for speaker identification — the LLM needs the full transcript to
      // identify anonymous speakers; selectedSpeakers is still used below for
      // name resolution in the formatted output (helpful context for the LLM).
      const skipSpeakerFilter =
        key === 'Content Writer | Identify Speaker Names';
      if (
        selectedSpeakers &&
        selectedSpeakers.length > 0 &&
        !skipSpeakerFilter
      ) {
        // Filter elements to include only those from selected speakers
        const filteredElements = elements.filter(
          (el: any) =>
            el.speaker_label &&
            selectedSpeakers?.some(s => s.speakerLabel === el.speaker_label),
        );

        // If filtering removed all elements, warn but continue with all elements
        if (filteredElements.length === 0) {
          this.logger.warn(
            'Speaker filtering removed all elements. Using all transcript elements.',
          );
        } else {
          elements = filteredElements;
        }
      }

      // Format transcript with speaker information whenever speaker labels exist
      const hasSpeakerLabels = elements.some((el: any) => el.speaker_label);
      // Resolve speaker names via the useSpeakerLabels module cache populated
      // by UI components; same source of truth as on-screen labels.
      const storyId = this.videoCreator.story?.id;

      if (
        (key === 'Content Writer | Generate Speaker Bio' || hasSpeakerLabels) &&
        elements.length > 0
      ) {
        // Helper to resolve speaker label → human-readable name
        const resolveSpeakerName = (label: string): string => {
          // 1. Try selectedSpeakers (user-selected, has resolved names)
          const fromSelected = selectedSpeakers?.find(
            s =>
              s.speakerLabel === label || s.allSpeakerLabels?.includes(label),
          )?.name;
          if (fromSelected) return fromSelected;
          // 2. Read from useSpeakerLabels cache; falls back to `Speaker N+1`
          //    for unmapped labels (or the raw label for non-`spk_*` shapes).
          return readSpeakerNameFromCache(storyId, label);
        };

        // Group contiguous elements by speaker
        let formattedText = '';
        let currentSpeaker = '';
        let currentText = '';

        // Process each element
        elements.forEach((element: any) => {
          if (!element || !element.speaker_label) return;

          const speakerLabel = element.speaker_label;
          const elementText = element.value || '';

          if (!elementText.trim()) return;

          if (speakerLabel !== currentSpeaker) {
            if (currentSpeaker && currentText) {
              formattedText += `${resolveSpeakerName(currentSpeaker)}: ${currentText}\n\n`;
            }
            currentSpeaker = speakerLabel;
            currentText = elementText;
          } else {
            currentText += /^[.,;:!?)\]}]/.test(elementText)
              ? elementText
              : ` ${elementText}`;
          }
        });

        // Flush the last speaker's accumulated text. Must run outside the
        // forEach above because the loop early-returns on whitespace/empty
        // elements — if the transcript's final element happens to be a
        // trailing whitespace punct (common), the in-loop flush would be
        // skipped and the last speaker's entire section would be lost.
        if (currentSpeaker && currentText) {
          formattedText += `${resolveSpeakerName(currentSpeaker)}: ${currentText}`;
        }

        this.logger.debug('Using formatted transcript with speakers', {
          elementCount: elements.length,
        });

        // Fail-safe check for empty formatted text
        if (formattedText && formattedText.trim().length > 0) {
          return formattedText.trim();
        }
        this.logger.warn(
          'Formatted text is empty, falling back to default formatting',
        );
      }

      // Default formatting - just join all text
      this.logger.info('Using default transcript formatting');
      const defaultText = elements
        .map((e: any) => e?.value || '')
        .join('')
        .trim();

      // Final safety check
      if (!defaultText) {
        this.logger.warn('Default transcript formatting produced empty text');
        return 'No transcript text available';
      }

      return defaultText;
    } catch (error) {
      this.logger.error('Error in getTranscript method:', error);
      return 'Error processing transcript';
    }
  };

  private async getResponseInputData(params: GenerateStreamResponseParams) {
    const {
      key,
      template,
      version = '1',
      type = 'existing',
      customInstructions,
      revision,
      brandVoice,
      avoidWords,
      selectedSpeakers,
      selectedGoal,
      selectedAudiences,
      selectedLanguage: selectedLanguageCode,
    } = params;
    const transcript = this.getTranscript(key, selectedSpeakers);
    let aiPrompt = template;
    if (!aiPrompt && customInstructions) {
      aiPrompt = await this.fetchAiPrompt(`Custom ${key}`);
    }
    if (!aiPrompt) {
      aiPrompt = await this.fetchAiPrompt(key);
    }

    if (aiPrompt) {
      aiPrompt = await this.enrichPromptIfNeeded(aiPrompt);
    }

    if (!aiPrompt || !transcript) return;
    let existingResponse: any =
      type === 'new' ? '' : this.getExistingResponseData(key);
    if (version === '2') {
      existingResponse = { ...aiPrompt, response: existingResponse.response };
    }

    let systemMessage = aiPrompt.description;
    if (customInstructions) {
      if (template) {
        systemMessage += `\n\nAdditional customization: ${customInstructions}\n\nIf the above customization contains keywords or themes, focus the content on those topics. If it contains specific instructions or directives, follow those instructions while maintaining the theme's style and format.`;
      } else {
        systemMessage += `\n\n${customInstructions}`;
      }
    }

    let followUp = '';
    if (type === 'regenerate') followUp += aiPrompt.followUp;
    if (revision) followUp += `${followUp ? '\n\n' : ''}${revision}`;

    // Add goal context if a goal is selected
    if (
      selectedGoal &&
      selectedGoal.id !== 'none' &&
      selectedGoal.finalPrompt
    ) {
      const goalPrompt = await this.fetchAiPrompt(
        'Content Writing | Context | Goal',
      );
      if (goalPrompt) {
        systemMessage = `\n\n${systemMessage}\n\n${goalPrompt.description}\n\n${selectedGoal.label}: ${selectedGoal.finalPrompt}`;
      }
    }

    // Add audience context if audiences are selected
    if (selectedAudiences && selectedAudiences.length > 0) {
      const audiencePrompt = await this.fetchAiPrompt(
        'Content Writing | Context | Audiences',
      );
      if (audiencePrompt) {
        // Concatenate all audience prompts
        const combinedAudiencePrompts = selectedAudiences
          .map(audience => `${audience.label}: ${audience.finalPrompt}`)
          .join('\n\n');
        systemMessage = `\n\n${systemMessage}\n\n${audiencePrompt.description}\n\n${combinedAudiencePrompts}`;
      }
    }

    // Add speaker context to the prompt if filtering is applied
    if (selectedSpeakers && selectedSpeakers.length > 0) {
      // Find speaker labels from transcript
      const allElements =
        this.transcriptionStore.originalTranscription?.elements || [];
      const allSpeakerLabels = Array.from(
        new Set(
          allElements
            .filter((el: any) => el.speaker_label)
            .map((el: any) => el.speaker_label),
        ),
      );

      // Only add filtering instructions if not selecting all speakers
      if (
        selectedSpeakers.length < allSpeakerLabels.length ||
        key === 'Quotes'
      ) {
        // Format speaker names for display in prompt
        const speakerNames = selectedSpeakers
          .map(s => s.name || `Speaker ${s.speakerLabel.replace('spk_', '')}`)
          .join(', ');

        // Base speaker context
        let speakerContext = '';

        const speakerTitlePrompt = await this.fetchAiPrompt('Speaker Title');
        if (speakerTitlePrompt) {
          systemMessage += `${systemMessage ? '\n\n' : ''}${
            speakerTitlePrompt.description
          }\nSpeaker titles: \n`;
          selectedSpeakers.forEach(speaker => {
            if (speaker.title) {
              systemMessage += `${speaker.name}: ${speaker.title}\n`;
            }
          });
        }

        const speakerBioPrompt = await this.fetchAiPrompt('Speaker Bio');
        if (speakerBioPrompt) {
          systemMessage += `${systemMessage ? '\n\n' : ''}${
            speakerBioPrompt.description
          }\nSpeaker bios: \n`;
          selectedSpeakers.forEach(speaker => {
            if (speaker.bio) {
              systemMessage += `${speaker.name}: ${speaker.bio}\n`;
            }
          });
        }

        // Customize speaker instructions based on content type

        switch (key) {
          case 'Quotes':
            const formattingInstruction = `IMPORTANT: End each quote with the speaker's name and title in the format " --- Speaker Name|Speaker Title" OR " --- Speaker Name" if title was not provided. For example: "This is an important quote. --- John Smith|CEO". EVERY quote MUST be formatted this way with exactly 3 dashes AND a vertical bar IF title is present to ensure proper attribution.`;
            systemMessage += `${
              systemMessage ? '\n\n' : ''
            }${formattingInstruction}`;
            speakerContext =
              selectedSpeakers.length > 0
                ? `Please generate quotes ONLY using the words spoken by ${speakerNames}. Ignore content from other speakers.`
                : 'Please generate quotes ONLY using the words spoken by the speakers in the transcript.';
            break;

          case 'Blog':
            speakerContext = `Focus the blog content primarily on perspectives and insights from ${speakerNames}. While you may reference other speakers for context, make ${speakerNames} the central voice(s) of the blog. When including direct quotes, attribute them properly to the speaker.`;
            break;

          case 'Social Post':
            speakerContext = `Focus the social post on the perspectives and insights from ${speakerNames}. If directly quoting, include attribution to the speaker.`;
            break;

          case 'Email':
            speakerContext = `Frame the email content around insights from ${speakerNames}, using their perspectives as the main value proposition while maintaining a professional email tone. Include relevant quotes with proper attribution when appropriate.`;
            break;

          case 'Reel Outline':
            speakerContext = `Focus the reel primarily on moments featuring ${speakerNames}. Other speakers may appear briefly for context, but the narrative should center on these speakers.`;
            break;

          // Add default case for other content types
          default:
            speakerContext = `Focus on content from ${speakerNames} when generating your response. Prioritize their perspectives and insights over other speakers in the transcript.`;
        }

        // Add the speaker context to the prompt
        systemMessage += `\n\n${speakerContext}`;

        // Add voice descriptions if available for selected speakers
        const speakersWithVoice = selectedSpeakers.filter(
          s => s.voice && s.voice.trim().length > 0,
        );

        if (speakersWithVoice.length > 0) {
          let voiceContext =
            '\n\nWhen generating content, match these speaking styles:';

          speakersWithVoice.forEach(speaker => {
            const speakerName =
              speaker.name ||
              `Speaker ${speaker.speakerLabel.replace('spk_', '')}`;
            voiceContext += `\n- ${speakerName}: ${speaker.voice}`;
          });

          // Add specific instructions based on content type
          switch (key) {
            case 'Quotes':
              voiceContext += `\n\nUse these voice descriptions to select quotes that best represent each speaker's unique voice and style.`;
              break;

            case 'Blog':
              voiceContext += `\n\nWhen crafting the blog, ensure the overall tone reflects these voice characteristics, especially when paraphrasing their perspectives.`;
              break;

            case 'Social Post':
              voiceContext += `\n\nMatch these voice characteristics when writing the post to maintain authenticity.`;
              break;

            case 'Email':
              voiceContext += `\n\nWhen writing the email, incorporate these voice characteristics into the messaging while maintaining professionalism.`;
              break;

            case 'Reel Outline':
              voiceContext += `\n\nWhen designing the reel outline, consider the brand voice to select moments that best represent the organization's communication style and values.`;
              break;
          }

          // Add the voice context to the prompt
          systemMessage += voiceContext;
        }
      }
    }

    if (brandVoice) {
      const brandVoicePrompt = await this.fetchAiPrompt('Brand Voice');
      if (brandVoicePrompt) {
        systemMessage += `${systemMessage ? '\n\n' : ''}${
          brandVoicePrompt.description
        }\nBrand voice: ${brandVoice}`;
      }
    }

    if (avoidWords) {
      const avoidWordsPrompt = await this.fetchAiPrompt('Avoid Words');
      if (avoidWordsPrompt) {
        systemMessage += `${systemMessage ? '\n\n' : ''}${
          avoidWordsPrompt.description
        }\nAvoid words: ${avoidWords}`;
      }
    }

    // Use selected language
    if (selectedLanguageCode) {
      const languageName = ORIGINAL_VIDEO_LANGUAGE_OPTIONS.find(
        lang => lang.value === selectedLanguageCode,
      )?.caption;
      if (languageName) {
        systemMessage += `${
          systemMessage ? '\n\n' : ''
        }${`Generate the content in the following language: ${languageName}`}`;
      }
    }

    // Add target word count or character count instruction (mutually exclusive, word count takes priority).
    // On revisions the user's instructions override the template target — injecting
    // a fixed target here would conflict with "make it shorter/longer" requests.
    if (type !== 'regenerate') {
      const countInstruction = buildAdjustedCountInstruction(
        aiPrompt.targetWordCount,
        aiPrompt.targetCharacterCount,
      );
      if (countInstruction) {
        systemMessage += `${systemMessage ? '\n\n' : ''}${countInstruction}`;
      }
    }

    // Add impact score instructions for Quotes
    if (key === 'Quotes') {
      const impactScorePrompt = await this.fetchAiPrompt('Quotes Impact Score');
      this.logger.info('[Quotes Impact Score] prompt lookup result:', {
        found: !!impactScorePrompt,
        hasDescription: !!impactScorePrompt?.description,
        title: impactScorePrompt?.title,
        descriptionPreview: impactScorePrompt?.description?.substring(0, 100),
        promptFields: impactScorePrompt?.promptFields?.map(f => f.name),
      });
      if (impactScorePrompt?.description) {
        systemMessage += `${systemMessage ? '\n\n' : ''}${
          impactScorePrompt.description
        }`;

        // Build score keys from promptFields (sub-scores + total), matching Clip Producer pattern
        const scoreKeys =
          impactScorePrompt.promptFields?.map(f => f.name) || [];
        if (scoreKeys.length > 0) {
          const jsonExample = scoreKeys.reduce(
            (acc, k) => ({ ...acc, [k]: 85 }),
            {} as Record<string, number>,
          );
          systemMessage += `\n\nIMPORTANT: At the end of each quote line, append scores as a JSON object in the exact format [SCORES:${JSON.stringify(
            jsonExample,
          )}] where each value is an integer from 0 to 100. The keys must be exactly: ${scoreKeys
            .map(k => `"${k}"`)
            .join(
              ', ',
            )}. For example: "This is a powerful quote --- Speaker Name|Speaker Title|[SCORES:${JSON.stringify(
            jsonExample,
          )}]". Every quote MUST end with this scores tag.`;
        } else {
          // Fallback: single score if no promptFields configured
          systemMessage +=
            '\n\nIMPORTANT: At the end of each quote line, append an impact score in the exact format [SCORES:{"impact":85}] where the value is an integer from 0 to 100. For example: "This is a powerful quote --- Speaker Name|Speaker Title|[SCORES:{"impact":85}]". Every quote MUST end with this scores tag.';
        }
        this.logger.info(
          '[Quotes Impact Score] instructions appended to system message',
        );
      } else {
        this.logger.warn(
          '[Quotes Impact Score] prompt not found or has no description — impact scores will NOT be generated. Make sure a DatoCMS AI Prompt with title exactly "Quotes Impact Score" exists and has a description.',
        );
      }
    }

    return {
      systemMessage,
      transcript,
      existingResponse,
      followUp,
      aiPrompt,
    };
  }

  private updateGeneratedContent = (
    key: SupportedGeneratedContentTypes,
    response: string = '',
  ) => {
    const generatedContent = this.videoCreator.contentStudioGeneratedContent;
    this.videoCreator.contentStudioGeneratedContent = {
      ...(generatedContent || {}),
      [key]: {
        ...(generatedContent?.[key] || {}),
        content: {
          ...(generatedContent?.[key]?.content || {}),
          response,
        },
      },
    } as ContentViewData;
  };

  public async regenerateStreamResponse(
    params: GenerateStreamResponseParams & {
      useSearch?: boolean;
      setLoading: (e: boolean) => void;
      onError?: (message: string) => void;
      responseFormat?: 'speaker_id';
      useBetterReasoning?: boolean;
    },
  ) {
    const {
      key,
      template,
      setLoading,
      onError,
      version = '1',
      type = 'existing',
      customInstructions,
      revision,
      brandVoice,
      avoidWords,
      selectedSpeakers,
      speakerFilterActive,
      selectedGoal,
      selectedAudiences,
      useSearch,
      selectedLanguage,
      responseFormat,
      useBetterReasoning,
      model,
      aiModel,
      skipStoryValidation,
    } = params;
    const aiTracker = AIEventTracker.start('ai_content_generation', {
      prompt_title: template?.title,
      prompt_type: key,
      prompt_id: template?.id,
      story_name: this.videoCreator.story?.title,
    });
    const logContext = this.getPromptLogContext();
    const promptLog = AIPromptLogger.start(this.datoClient, {
      prompt_title: template?.title || key,
      prompt_type: key,
      aiPromptId: template?.id,
      modelRecordId: aiModel?.id || template?.model?.id,
      ...logContext,
      metadata: {
        ...logContext.metadata,
        prompt_title: template?.title || key,
        prompt_type: template?.promptType || key,
        prompt_id: template?.id,
        endpoint: 'regenerate-gpt-response-stream',
        content_producer_selections: {
          ...(customInstructions && { custom_theme: customInstructions }),
          ...(selectedGoal && {
            goal: { id: selectedGoal.id, label: selectedGoal.label },
          }),
          ...(selectedAudiences?.length && {
            audiences: selectedAudiences.map(a => ({
              id: a.id,
              label: a.label,
            })),
          }),
          ...(selectedSpeakers?.length && {
            speakers: selectedSpeakers.map(s => ({
              id: s.id,
              name: s.speakerLabel,
              title: s.title,
            })),
          }),
          speaker_filter_active:
            speakerFilterActive ??
            (selectedSpeakers !== undefined && selectedSpeakers.length > 0),
          brand_voice: !!brandVoice,
          generate_from_clip: false,
        },
      },
    });
    // Capture the per-call AbortController identity so error handlers can tell whether a newer call has already taken over.
    let myController: AbortController | null = null;
    const isStillActive = () =>
      myController !== null && this.controller === myController;

    try {
      // Cancel any in-progress requests
      if (this.controller) {
        this.controller.abort();
        this.controller = null;
      }

      // Clear existing content before making the request
      this.updateGeneratedContent(key, '');

      // Create a new abort controller and capture its identity.
      this.controller = new AbortController();
      myController = this.controller;
      const { signal } = myController;

      // Prepare the request data
      const items = await this.getResponseInputData({
        key,
        template,
        version,
        type,
        customInstructions,
        revision,
        brandVoice,
        avoidWords,
        selectedSpeakers,
        selectedGoal,
        selectedAudiences,
        selectedLanguage,
      });

      // Check if we have all the required data
      if (!items) {
        console.error(
          'getResponseInputData returned null or undefined. API request aborted.',
        );
        if (isStillActive()) setLoading(false);
        return;
      }

      const {
        systemMessage,
        transcript,
        existingResponse,
        followUp,
        aiPrompt,
      } = items;
      const storyId = this.videoCreator.story?.id;

      // Validate required data
      if (!storyId && !skipStoryValidation) {
        console.error('No story ID available. API request aborted.');
        if (isStillActive()) setLoading(false);
        return;
      }

      if (!transcript || transcript.trim().length === 0) {
        console.error('No transcript text available. API request aborted.');
        if (isStillActive()) setLoading(false);
        return;
      }

      // console.log('Got response input data:', {
      //   hasSystemMessage: !!systemMessage,
      //   hasTranscript: !!transcript,
      //   transcriptLength: transcript?.length || 0,
      //   hasExistingResponse: !!existingResponse,
      //   hasFollowUp: !!followUp,
      //   storyId,
      // });

      // Construct API URL
      const apiUrl = `${
        process.env.REACT_APP_API_URL
      }/api/aiprompts/regenerate-gpt-response-stream${
        version === '2' ? '-v2' : ''
      }`;
      // console.log('Calling API at:', apiUrl);

      const flagModel = this.videoCreator.getStringFlag(
        'contentGenerationChatGptModel',
      );
      const resolved = resolveAIModel(
        aiModel,
        aiPrompt?.model,
        model ?? flagModel,
      );

      // Resolve model DatoCMS record ID for prompt log (fallback to lookup by modelId string)
      let resolvedModelRecordId = aiModel?.id || aiPrompt?.model?.id;
      if (!resolvedModelRecordId && resolved?.model_id) {
        try {
          const allModels =
            await this.videoCreator.rootStore.datoClientStore.aiModelRepository.getAllModels();
          const match = allModels.find(m => m.modelId === resolved.model_id);
          if (match) resolvedModelRecordId = match.id;
        } catch (err) {
          this.logger.debug(
            'Failed to resolve model record ID for prompt log',
            err,
          );
        }
      }

      // User context for backend logging (PLAT-9145)
      const identity =
        this.videoCreator.rootStore.userIdentityStore.currentIdentity;

      // Prepare request body
      const requestBody = {
        systemMessage,
        transcript,
        existingResponse,
        followUp,
        storyId,
        useSearch,
        responseFormat,
        useBetterReasoning,
        model: resolved?.model_id,
        ...spreadModelParams(resolved),
        prompt_title: aiPrompt?.title,
        prompt_internal_title: aiPrompt?.internalTitle,
        prompt_type: aiPrompt?.promptType,
        prompt_id: aiPrompt?.id,
        story_name: this.videoCreator.story?.title,
        user_email: identity?.email,
        user_id: identity?.id,
      };

      try {
        const fetchT0 = performance.now();
        this.logger.info('starting fetch to regenerate-gpt-response-stream', {
          apiUrl,
          provider: requestBody.provider,
          model_id: requestBody.model_id,
          model: requestBody.model,
          aiPromptModel: aiPrompt?.model
            ? {
                internalName: aiPrompt.model.internalName,
                displayName: aiPrompt.model.displayName,
                modelId: aiPrompt.model.modelId,
                provider: aiPrompt.model.providerLink?.internalName,
              }
            : null,
          aiModelOverride: aiModel
            ? {
                internalName: aiModel.internalName,
                modelId: aiModel.modelId,
                provider: aiModel.providerLink?.internalName,
              }
            : null,
          flagModel,
          modelArg: model,
          promptTitle: aiPrompt?.title,
          promptInternalTitle: aiPrompt?.internalTitle,
        });

        // Make API request
        const res = await fetch(apiUrl, {
          signal,
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
        });

        const firstContentLatencyMs = Math.round(performance.now() - fetchT0);
        this.logger.info(
          `response received status=${res.status} +${firstContentLatencyMs}ms (time-to-first-byte)`,
        );

        // Handle error responses
        if (!res.ok) {
          let errorText = '';
          try {
            errorText = await res.text();
          } catch (parseError) {
            errorText = `<failed to read body: ${(parseError as Error)?.message ?? 'unknown'}>`;
          }
          this.logger.error('regenerate-gpt-response-stream rejected', {
            apiUrl,
            status: res.status,
            statusText: res.statusText,
            provider: requestBody.provider,
            model_id: requestBody.model_id,
            model: requestBody.model,
            promptTitle: aiPrompt?.title,
            errorBody: errorText.slice(0, 2000),
            errorBodyLength: errorText.length,
            errorBodyEmpty: errorText.length === 0,
          });
          const safeSnippet = errorText
            ? ` - ${stripHtml(errorText).slice(0, 200)}`
            : ' (empty body — check backend logs)';
          if (isStillActive()) {
            this.updateGeneratedContent(key, '');
            onError?.(
              `Generation failed: ${res.status} ${res.statusText}${safeSnippet}`,
            );
            setLoading(false);
          }
          return;
        }

        // Process the response stream
        const data = res.body;
        if (!data) {
          console.error('Response body is null or undefined');
          if (isStillActive()) {
            this.updateGeneratedContent(key, '');
            onError?.('Generation failed: no response data received');
            setLoading(false);
          }
          return;
        }

        // Set up stream processing
        const reader = data.getReader();
        const decoder = new TextDecoder();

        this.markContentAsGenerated(key, selectedSpeakers, selectedLanguage);

        // Process the stream
        await this.processGeneratedContentByChunk(
          reader,
          decoder,
          setLoading,
          key,
          key, // Use the key as the logging segment
          isStillActive,
        );
        aiTracker.complete({
          provider: resolved?.provider,
          model_id: resolved?.model_id,
          prompt_title: aiPrompt?.title,
          prompt_id: aiPrompt?.id,
        });
        promptLog.complete({
          provider: resolved?.provider || 'openai',
          model_id: resolved?.model_id || 'unknown',
          modelRecordId: resolvedModelRecordId,
          first_content_latency_ms: firstContentLatencyMs,
        });
      } catch (fetchError: any) {
        // Successor stream has taken over
        if (!isStillActive()) return;
        // Intentional abort by a newer call
        if (fetchError?.name === 'AbortError') {
          return;
        }
        this.logger.error('Fetch error', extractErrorInfo(fetchError));
        this.updateGeneratedContent(key, '');
        onError?.(
          `Generation failed: ${fetchError.message ?? 'unknown error'}`,
        );
        aiTracker.fail(fetchError);
        promptLog.fail(fetchError);
        setLoading(false);
      }
    } catch (err: any) {
      // Handle top-level errors
      if (err.name === 'AbortError') {
        console.log('Request aborted', err.message);
        return;
      }
      if (!isStillActive()) return;
      console.error('Error occurred:', err);
      this.updateGeneratedContent(key, '');
      onError?.(`Generation failed: ${err.message ?? 'unknown error'}`);
      aiTracker.fail(err);
      promptLog.fail(err);
      setLoading(false);
    }
  }

  /**
   * Regenerates content using a clip-specific transcript as primary input,
   * with the full story transcript passed as additional context.
   */
  public async regenerateFromClipTranscript(
    params: GenerateStreamResponseParams & {
      setLoading: (e: boolean) => void;
      onError?: (message: string) => void;
      clipTranscript: string;
    },
  ) {
    const { clipTranscript, customInstructions, key, ...restParams } = params;

    const fullTranscript = this.getTranscript(key);
    const originalGetTranscript = this.getTranscript.bind(this);
    this.getTranscript = () => clipTranscript;

    const contentTypeLabelMap: Record<string, string> = {
      Quotes: 'quotes',
      Email: 'email',
      Blog: 'article',
      'Reel Outline': 'reel outline',
    };
    const contentTypeLabel = contentTypeLabelMap[key ?? ''] ?? 'social post';
    const clipContext = [
      'The transcript provided is from a specific video clip within a longer story.',
      `Use it as the primary source for the ${contentTypeLabel}.`,
      `Use the full story transcript below to add broader context, background, and depth to the ${contentTypeLabel}.`,
      fullTranscript
        ? `\n\nFull Story Transcript (use to enrich and add context to the post):\n${fullTranscript}`
        : '',
    ].join(' ');

    const effectiveCustomInstructions = customInstructions
      ? `${clipContext}\n\n${customInstructions}`
      : clipContext;

    try {
      await this.regenerateStreamResponse({
        ...restParams,
        key,
        customInstructions: effectiveCustomInstructions,
      });
    } finally {
      this.getTranscript = originalGetTranscript;
    }
  }

  private markContentAsGenerated(
    key: SupportedGeneratedContentTypes,
    selectedSpeakers?: Speaker[],
    selectedLanguage?: string | null,
  ) {
    if (this.videoCreator.contentStudioGeneratedContent?.[key]) {
      this.videoCreator.contentStudioGeneratedContent[key].hasBeenGenerated =
        true;
      this.videoCreator.contentStudioGeneratedContent[key].usedSpeakers =
        !!selectedSpeakers?.length;
      this.videoCreator.contentStudioGeneratedContent[key].selectedLanguage =
        selectedLanguage;
    }
  }

  public async matchPunchListWithSources(
    punchList: PunchListItem[],
    sources: string[],
  ): Promise<{ punchList: PunchListItem[] }> {
    const logContext = this.getPromptLogContext();
    const promptLog = AIPromptLogger.start(this.datoClient, {
      prompt_title: 'Match Punch List With Sources',
      prompt_type: 'punch_list',
      ...logContext,
      metadata: {
        ...logContext.metadata,
        prompt_title: 'Match Punch List With Sources',
        prompt_type: 'SYSTEM',
        endpoint: 'match-punchlist-with-sources',
      },
    });
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/aiprompts/match-punchlist-with-sources`,
        { punchList, sources, storyId: this.videoCreator.story?.id },
      );
      promptLog.complete({ provider: 'openai', model_id: 'default' });
      return data;
    } catch (err) {
      promptLog.fail(err);
      throw err;
    }
  }

  public async generatePunchListItem(line: string) {
    const storyId = this.videoCreator.story?.id;
    if (!line || !storyId) return;

    const logContext = this.getPromptLogContext();
    const promptLog = AIPromptLogger.start(this.datoClient, {
      prompt_title: 'Generate Punch List Item',
      prompt_type: 'punch_list',
      ...logContext,
      metadata: {
        ...logContext.metadata,
        prompt_title: 'Generate Punch List Item',
        prompt_type: 'SYSTEM',
        endpoint: 'generate-punchlist-item',
      },
    });
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/aiprompts/generate-punchlist-item`,
        { line, storyId },
      );
      promptLog.complete({ provider: 'openai', model_id: 'default' });
      return data;
    } catch (err) {
      promptLog.fail(err);
      throw err;
    }
  }

  private async processGeneratedContentByChunk(
    reader: any,
    decoder: TextDecoder,
    setLoading: (e: boolean) => void,
    key: SupportedGeneratedContentTypes,
    loggingKey: string,
    isStillActive: () => boolean = () => true,
  ) {
    let i = 0;
    const t0 = performance.now();
    this.logger.info(`processGeneratedContentByChunk started for key="${key}"`);
    while (!this.streamProcessDone) {
      const { value, done: doneReading } = await reader.read();
      // A newer call has taken over
      if (!isStillActive()) return;
      this.streamProcessDone = doneReading;
      let chunk = decoder.decode(value);
      const elapsed = (performance.now() - t0).toFixed(1);
      if (i <= 3 || i % 20 === 0 || doneReading) {
        this.logger.info(
          `chunk #${i} len=${chunk.length} done=${doneReading} +${elapsed}ms`,
        );
      }
      let addition = '';

      if (key === 'Email') {
        if (i === 0 && !chunk.includes('##')) addition = '## ';
        if (chunk === ' ') chunk = '\n\n';
        if (chunk.includes('Subject:')) {
          chunk = chunk.replace('Subject:', '').trim();
        }
      } else if (key === 'Blog') {
        this.videoCreator.selectedGeneratedContent.blog = {
          type: doneReading ? 'generated' : 'generating',
        };
      } else if (key === 'Social Post') {
        this.videoCreator.selectedGeneratedContent.socialPost = {
          type: doneReading ? 'generated' : 'generating',
        };
      } else if (key === 'Quotes') {
        // Ensure each quote has the proper formatting if it doesn't already
        if (doneReading) {
          // Access the full text after processing is complete
          const existingResponse = this.getExistingResponseData(key);
          // console.log('Existing response:', existingResponse);
        }
      } else if (key === 'Reel Outline') {
        this.videoCreator.selectedGeneratedContent.reelOutline = {
          type: doneReading ? 'generated' : 'generating',
        };
      }

      const existingResponse = this.getExistingResponseData(key);
      const text = existingResponse + addition + chunk;
      this.updateGeneratedContent(key, text);
      if (doneReading) {
        logChatGptOutput({ [loggingKey]: text });
        setLoading(false);
      }
      i++;
    }
    if (this.streamProcessDone) {
      // Only clear the controller if we still own it.
      if (isStillActive()) {
        this.controller = null;
      }
      this.streamProcessDone = false;
    }
  }

  async generateTalkingPoint({
    setLoading,
    brandText,
    key = null,
    template,
    avoidWords,
    aiModel,
  }: {
    setLoading: (e: boolean) => void;
    brandText?: string;
    key?: (keyof TalkingPointContent & 'all') | null;
    template?: AIPrompt | null;
    avoidWords?: string;
    aiModel?: AIModel | null;
  }) {
    const tpTracker = AIEventTracker.start('ai_content_generation', {
      provider: aiModel?.providerLink?.internalName,
      model_id: aiModel?.modelId,
      prompt_title: template?.title,
      prompt_type: 'TalkingPoints',
      prompt_id: template?.id,
      story_name: this.videoCreator.story?.title,
    });
    const logContext = this.getPromptLogContext();
    const promptLog = AIPromptLogger.start(this.datoClient, {
      prompt_title: template?.title || 'Talking Points',
      prompt_type: 'TalkingPoints',
      aiPromptId: template?.id,
      modelRecordId: aiModel?.id || template?.model?.id,
      ...logContext,
      metadata: {
        ...logContext.metadata,
        prompt_title: template?.title || 'Talking Points',
        prompt_type: template?.promptType || 'TalkingPoints',
        prompt_id: template?.id,
        endpoint: 'regenerate-gpt-response-stream-v2',
      },
    });
    try {
      if (this.talkingPointController) {
        this.talkingPointController.abort();
      }
      this.talkingPointController = new AbortController();
      const { signal } = this.talkingPointController;

      const transcript = this.transcriptionStore
        .getFinalTranscriptionElements()
        ?.map(e => e.value || '')
        .join('');

      let prompts = template;
      if (!prompts) {
        prompts = await this.fetchAiPrompt('Fundraising prompt');
      }
      if (!prompts?.promptFields || !transcript) return;

      prompts = await this.enrichPromptIfNeeded(prompts);

      const flagModel = this.videoCreator.getStringFlag(
        'contentGenerationChatGptModel',
      );
      const resolved = resolveAIModel(aiModel, prompts?.model, flagModel);
      const temperature = 1;

      if (!key || key === 'all') {
        if (!key || !this.videoCreator.talkingPointContent) {
          this.videoCreator.talkingPointContent = {} as TalkingPointContent;
        } else {
          this.videoCreator.talkingPointContent = Object.fromEntries(
            Object.entries(this.videoCreator.talkingPointContent).map(
              ([key, value]) => [key, { prompt: value.prompt, content: '' }],
            ),
          ) as TalkingPointContent;
        }

        for (const prompt of prompts.promptFields) {
          await this.runTalkingPointAi(
            transcript,
            prompts.description,
            setLoading,
            prompt,
            temperature,
            resolved,
            key,
            brandText,
            avoidWords,
            signal,
            prompts.title,
            prompts.id,
            prompts.internalTitle,
            prompts.promptType,
          );
        }
      } else {
        const prompt = prompts.promptFields.find(f => f.name === key);
        if (!prompt) return;

        await this.runTalkingPointAi(
          transcript,
          prompts.description,
          setLoading,
          prompt,
          temperature,
          resolved,
          key,
          brandText,
          avoidWords,
          signal,
          prompts.title,
          prompts.id,
          prompts.internalTitle,
          prompts.promptType,
        );
      }
      tpTracker.complete({
        provider: resolved?.provider,
        model_id: resolved?.model_id,
        prompt_title: prompts?.title,
        prompt_id: prompts?.id,
      });
      promptLog.complete({
        provider: resolved?.provider || 'openai',
        model_id: resolved?.model_id || 'unknown',
        modelRecordId: aiModel?.id,
      });
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('Request aborted', error, error.message);
      } else {
        console.error('Error occurred:', error);
        tpTracker.fail(error);
        promptLog.fail(error);
      }
    }
  }

  getPrevContent(content: TalkingPointContent | null, promptName: string) {
    try {
      if (content && promptName in content) {
        return content[promptName as keyof TalkingPointContent];
      }
      return { prompt: '', content: '' };
    } catch (error) {
      console.log('Error: ', error);
    }
  }

  async runTalkingPointAi(
    transcript: string,
    description: string,
    setLoading: (e: boolean) => void,
    prompt: AIPrompt['promptFields'][0],
    temperature: number,
    resolved: ResolvedModel | undefined,
    key: (keyof TalkingPointContent & 'all') | null,
    brandText: string | undefined,
    avoidWords: string | undefined,
    signal: AbortSignal,
    promptTitle?: string,
    promptId?: string,
    promptInternalTitle?: string | null,
    promptTypeName?: string | null,
  ) {
    let prevContent = '';
    let prevPrompt = '';
    if (key) {
      const prevData = this.getPrevContent(
        this.videoCreator.talkingPointContent,
        prompt.name,
      );
      prevContent = turndownService.turndown(prevData?.content ?? '');
      prevPrompt = prevData?.prompt ?? '';
    }

    const promptDescription = `${prompt.description}.  Return only markdown with no other comments.`;
    let currentPrompt = `${promptDescription}`;

    if (brandText) currentPrompt += ` ${brandText}`;

    if (avoidWords) {
      const avoidWordsPrompt = await this.fetchAiPrompt('Avoid Words');
      if (avoidWordsPrompt) {
        currentPrompt += `${currentPrompt ? '\n\n' : ''}${
          avoidWordsPrompt.description
        }: ${avoidWords}`;
      }
    }

    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/aiprompts/stream-talking-point-response`,
      {
        signal,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          transcript,
          systemMessage: description,
          prevPrompt: prevPrompt || promptDescription,
          currentPrompt,
          temperature,
          model: resolved?.model_id,
          ...spreadModelParams(resolved),
          prevContent,
          prompt_title: promptTitle,
          prompt_internal_title: promptInternalTitle,
          prompt_type: promptTypeName,
          prompt_id: promptId,
          storyId: this.videoCreator.story?.id,
          story_name: this.videoCreator.story?.title,
          user_email:
            this.videoCreator.rootStore.userIdentityStore.currentIdentity
              ?.email,
          user_id:
            this.videoCreator.rootStore.userIdentityStore.currentIdentity?.id,
        }),
      },
    );

    const data = res.body;
    if (!data) return;
    const reader = data.getReader();
    const decoder = new TextDecoder();
    let done = false;

    if (key && this.videoCreator.talkingPointContent?.hasOwnProperty(key)) {
      this.videoCreator.talkingPointContent[
        key as keyof TalkingPointContent
      ].content = '';
    }

    while (!done) {
      const { value, done: doneReading } = await reader.read();
      done = doneReading;
      const chunk = decoder.decode(value);
      setLoading(false);

      const key = prompt.name as keyof TalkingPointContent;
      const existingContent = (this.videoCreator.talkingPointContent ||
        {}) as TalkingPointContent;

      const content = (existingContent[key]?.content || '') + chunk;
      this.videoCreator.talkingPointContent = {
        ...(existingContent as TalkingPointContent),
        [key]: {
          content,
          prompt: currentPrompt,
        },
      };
      if (done) {
        logChatGptOutput({ 'stream-talking-point-response': content });
      }
    }
  }

  regenerateArtifactTitle = async ({
    artifactId,
    storyId,
    organizationId,
  }: {
    artifactId: string;
    storyId: string | undefined;
    organizationId: string | undefined;
  }) => {
    const logContext = this.getPromptLogContext();
    const promptLog = AIPromptLogger.start(this.datoClient, {
      prompt_title: 'Regenerate Artifact Title',
      prompt_type: 'artifact_title',
      ...logContext,
      metadata: {
        ...logContext.metadata,
        prompt_title: 'Regenerate Artifact Title',
        prompt_type: 'SYSTEM',
        endpoint: 'regenerate-artifact-title',
      },
    });
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/aiprompts/regenerate-artifact-title`,
        {
          artifactId,
          storyId,
          organizationId,
        },
      );
      promptLog.complete({ provider: 'openai', model_id: 'default' });
      return data;
    } catch (err) {
      promptLog.fail(err);
      throw err;
    }
  };

  public async generateSpeakerBio({
    speakerName,
    speakerRole,
    selectedSpeakers,
    customInstructions,
    existingBio,
  }: {
    speakerName: string;
    speakerRole?: string;
    selectedSpeakers?: Speaker[];
    customInstructions?: string;
    existingBio?: string;
  }): Promise<string> {
    // Create instructions based on whether this is initial generation or regeneration
    let instructions = `Focus on generating a bio for ${speakerName}${
      speakerRole ? `, ${speakerRole}` : ''
    }.`;

    if (customInstructions && existingBio) {
      // For regeneration, include the user's specific instructions
      instructions += `\n\nThe user has requested the following changes to the existing bio: ${customInstructions}`;
    }

    // Create a promise to collect the streamed response
    return new Promise((resolve, reject) => {
      let generatedBio = '';
      let isFirstChunk = true;

      // Override the updateGeneratedContent method temporarily to capture the response
      const originalUpdate = this.updateGeneratedContent.bind(this);
      this.updateGeneratedContent = (
        key: SupportedGeneratedContentTypes,
        response: string = '',
      ) => {
        if (key === 'Content Writer | Generate Speaker Bio') {
          generatedBio = response;
        }
        originalUpdate(key, response);
      };

      // Store existing bio in content studio if regenerating
      if (existingBio) {
        this.updateGeneratedContent(
          'Content Writer | Generate Speaker Bio',
          existingBio,
        );
      }

      // Use the existing regenerateStreamResponse method
      // The system will:
      // 1. Fetch the "Content Writer | Generate Speaker Bio" prompt from AI prompts
      // 2. Use its description as the base system message
      // 3. Append our customInstructions to personalize for the specific speaker
      this.regenerateStreamResponse({
        key: 'Content Writer | Generate Speaker Bio',
        type: existingBio ? 'regenerate' : 'new',
        customInstructions: instructions,
        useSearch: true,
        revision:
          customInstructions && existingBio ? customInstructions : undefined,
        selectedSpeakers, // Pass speakers for proper transcript formatting
        setLoading: (loading: boolean) => {
          if (!loading && generatedBio) {
            // Restore original method
            this.updateGeneratedContent = originalUpdate;
            resolve(generatedBio);
          }
        },
      }).catch(error => {
        // Restore original method
        this.updateGeneratedContent = originalUpdate;
        reject(error);
      });
    });
  }

  public async generateSpeakerVoice({
    speakerName,
    speakerRole,
    selectedSpeakers,
    customInstructions,
    existingVoice,
  }: {
    speakerName: string;
    speakerRole?: string;
    selectedSpeakers?: Speaker[];
    customInstructions?: string;
    existingVoice?: string;
  }): Promise<string> {
    // Create instructions based on whether this is initial generation or regeneration
    let instructions = `Focus on generating a voice description for ${speakerName}${
      speakerRole ? `, ${speakerRole}` : ''
    }.`;

    if (customInstructions && existingVoice) {
      // For regeneration, include the user's specific instructions
      instructions += `\n\nThe user has requested the following changes to the existing voice description: ${customInstructions}`;
    }

    // Create a promise to collect the streamed response
    return new Promise((resolve, reject) => {
      let generatedVoice = '';
      let isFirstChunk = true;

      // Override the updateGeneratedContent method temporarily to capture the response
      const originalUpdate = this.updateGeneratedContent.bind(this);
      this.updateGeneratedContent = (
        key: SupportedGeneratedContentTypes,
        response: string = '',
      ) => {
        if (key === 'Content Writer | Generate Speaker Voice') {
          generatedVoice = response;
        }
        originalUpdate(key, response);
      };

      // Store existing voice in content studio if regenerating
      if (existingVoice) {
        this.updateGeneratedContent(
          'Content Writer | Generate Speaker Voice',
          existingVoice,
        );
      }

      // Use the existing regenerateStreamResponse method
      this.regenerateStreamResponse({
        key: 'Content Writer | Generate Speaker Voice',
        type: existingVoice ? 'regenerate' : 'new',
        customInstructions: instructions,
        useSearch: true,
        revision:
          customInstructions && existingVoice ? customInstructions : undefined,
        selectedSpeakers, // Pass speakers for proper transcript formatting
        setLoading: (loading: boolean) => {
          if (!loading && generatedVoice) {
            // Restore original method
            this.updateGeneratedContent = originalUpdate;
            resolve(generatedVoice);
          }
        },
      }).catch(error => {
        // Restore original method
        this.updateGeneratedContent = originalUpdate;
        reject(error);
      });
    });
  }

  private getBrandKitData() {
    const branding = this.videoCreator.organization?.branding;
    if (!branding) return null;

    return {
      brandVoice: branding.brandVoice || '',
      avoidWords: branding.avoidWords || '',
      customVocabulary: branding.customVocabulary || '',
      organizationWebsite: branding.organizationWebsite || '',
      externalLinks: branding.externalLinks || '',
      primaryColors: branding.primaryColors || '',
      secondaryColors: branding.secondaryColors || '',
      // Existing goals and audiences for context
      existingGoals:
        branding.goals?.map(g => `${g.label}: ${g.finalPrompt}`).join('\n') ||
        '',
      existingAudiences:
        branding.audiences
          ?.map(a => `${a.label}: ${a.finalPrompt}`)
          .join('\n') || '',
    };
  }

  public async generateAudience({
    audienceName,
    customInstructions,
    existingAudience,
  }: {
    audienceName?: string;
    customInstructions?: string;
    existingAudience?: string;
  }): Promise<{ title?: string; description: string } | string> {
    const generateTitle = !existingAudience; // Only generate title for new audiences

    // Create instructions based on whether this is initial generation or regeneration
    let instructions = '';

    if (generateTitle) {
      // For new audiences, ask for both title and description
      instructions = audienceName
        ? `Generate a target audience based on: "${audienceName}". Return a JSON object with two fields:\n1. "title": A concise, descriptive title for this audience segment (2-5 words)\n2. "description": A detailed 2-3 paragraph description of the audience`
        : 'Generate a target audience. Return a JSON object with two fields:\n1. "title": A concise, descriptive title for this audience segment (2-5 words)\n2. "description": A detailed 2-3 paragraph description of the audience';
    } else {
      // For regeneration, only update the description
      instructions = audienceName
        ? `Focus on generating an audience description for "${audienceName}".`
        : 'Generate a target audience description based on the brand context.';

      if (customInstructions && existingAudience) {
        // For regeneration, include the user's specific instructions
        instructions += `\n\nThe user has requested the following changes to the existing audience description: ${customInstructions}`;
      }
    }

    // Create a promise to collect the streamed response
    return new Promise((resolve, reject) => {
      let generatedAudience = '';

      // Override the updateGeneratedContent method temporarily to capture the response
      const originalUpdate = this.updateGeneratedContent.bind(this);
      this.updateGeneratedContent = (
        key: SupportedGeneratedContentTypes,
        response: string = '',
      ) => {
        if (key === 'Content Writer | Generate Audience') {
          generatedAudience = response;
        }
        originalUpdate(key, response);
      };

      // Store existing audience in content studio if regenerating
      if (existingAudience) {
        this.updateGeneratedContent(
          'Content Writer | Generate Audience',
          existingAudience,
        );
      }

      // Get brand kit data
      const brandKitData = this.getBrandKitData();

      // Build context from brand kit
      let brandContext = '';
      if (brandKitData) {
        brandContext = `\n\nCompany Context:\n`;
        if (brandKitData.brandVoice) {
          brandContext += `Brand Voice: ${brandKitData.brandVoice}\n`;
        }
        if (brandKitData.organizationWebsite) {
          brandContext += `Website: ${brandKitData.organizationWebsite}\n`;
        }
        if (brandKitData.existingAudiences) {
          brandContext += `\nOther Defined Audiences:\n${brandKitData.existingAudiences}\n`;
        }
        if (brandKitData.existingGoals) {
          brandContext += `\nCompany Goals:\n${brandKitData.existingGoals}\n`;
        }
      }

      // Create a mock transcript that contains the brand context
      const brandKitTranscript =
        brandContext || 'No brand kit information available.';

      // Override the getTranscript method temporarily to return brand kit data
      const originalGetTranscript = this.getTranscript.bind(this);
      this.getTranscript = () => brandKitTranscript;

      // Use the existing regenerateStreamResponse method
      this.regenerateStreamResponse({
        key: 'Content Writer | Generate Audience',
        type: existingAudience ? 'regenerate' : 'new',
        customInstructions: instructions,
        useSearch: true,
        skipStoryValidation: true,
        revision:
          customInstructions && existingAudience
            ? customInstructions
            : undefined,
        setLoading: (loading: boolean) => {
          if (!loading && generatedAudience) {
            // Restore original methods
            this.updateGeneratedContent = originalUpdate;
            this.getTranscript = originalGetTranscript;

            // Try to parse JSON response for new audiences
            if (generateTitle) {
              try {
                const parsed = JSON.parse(generatedAudience);
                if (parsed.title && parsed.description) {
                  resolve(parsed);
                } else {
                  resolve({ description: generatedAudience });
                }
              } catch {
                resolve({ description: generatedAudience });
              }
            } else {
              resolve(generatedAudience);
            }
          }
        },
      }).catch(error => {
        // Restore original methods
        this.updateGeneratedContent = originalUpdate;
        this.getTranscript = originalGetTranscript;
        reject(error);
      });
    });
  }

  public async generateGoal({
    goalName,
    customInstructions,
    existingGoal,
  }: {
    goalName?: string;
    customInstructions?: string;
    existingGoal?: string;
  }): Promise<{ title?: string; description: string } | string> {
    const generateTitle = !existingGoal; // Only generate title for new goals

    // Create instructions based on whether this is initial generation or regeneration
    let instructions = '';

    if (generateTitle) {
      // For new goals, ask for both title and description
      instructions = goalName
        ? `Generate a business goal based on: "${goalName}". Return a JSON object with two fields:\n1. "title": A concise, descriptive title for this goal (2-5 words)\n2. "description": A detailed 2-3 paragraph description of the goal with strategies and metrics`
        : 'Generate a business goal. Return a JSON object with two fields:\n1. "title": A concise, descriptive title for this goal (2-5 words)\n2. "description": A detailed 2-3 paragraph description of the goal with strategies and metrics';
    } else {
      // For regeneration, only update the description
      instructions = goalName
        ? `Focus on generating a goal description for "${goalName}".`
        : 'Generate a goal description based on the brand context.';

      if (customInstructions && existingGoal) {
        // For regeneration, include the user's specific instructions
        instructions += `\n\nThe user has requested the following changes to the existing goal description: ${customInstructions}`;
      }
    }

    // Create a promise to collect the streamed response
    return new Promise((resolve, reject) => {
      let generatedGoal = '';

      // Override the updateGeneratedContent method temporarily to capture the response
      const originalUpdate = this.updateGeneratedContent.bind(this);
      this.updateGeneratedContent = (
        key: SupportedGeneratedContentTypes,
        response: string = '',
      ) => {
        if (key === 'Content Writer | Generate Goal') {
          generatedGoal = response;
        }
        originalUpdate(key, response);
      };

      // Store existing goal in content studio if regenerating
      if (existingGoal) {
        this.updateGeneratedContent(
          'Content Writer | Generate Goal',
          existingGoal,
        );
      }

      // Get brand kit data
      const brandKitData = this.getBrandKitData();

      // Build context from brand kit
      let brandContext = '';
      if (brandKitData) {
        brandContext = `\n\nCompany Context:\n`;
        if (brandKitData.brandVoice) {
          brandContext += `Brand Voice: ${brandKitData.brandVoice}\n`;
        }
        if (brandKitData.organizationWebsite) {
          brandContext += `Website: ${brandKitData.organizationWebsite}\n`;
        }
        if (brandKitData.existingGoals) {
          brandContext += `\nOther Defined Goals:\n${brandKitData.existingGoals}\n`;
        }
        if (brandKitData.existingAudiences) {
          brandContext += `\nTarget Audiences:\n${brandKitData.existingAudiences}\n`;
        }
      }

      // Create a mock transcript that contains the brand context
      const brandKitTranscript =
        brandContext || 'No brand kit information available.';

      // Override the getTranscript method temporarily to return brand kit data
      const originalGetTranscript = this.getTranscript.bind(this);
      this.getTranscript = () => brandKitTranscript;

      // Use the existing regenerateStreamResponse method
      this.regenerateStreamResponse({
        key: 'Content Writer | Generate Goal',
        type: existingGoal ? 'regenerate' : 'new',
        customInstructions: instructions,
        useSearch: true,
        skipStoryValidation: true,
        revision:
          customInstructions && existingGoal ? customInstructions : undefined,
        setLoading: (loading: boolean) => {
          if (!loading && generatedGoal) {
            // Restore original methods
            this.updateGeneratedContent = originalUpdate;
            this.getTranscript = originalGetTranscript;

            // Try to parse JSON response for new goals
            if (generateTitle) {
              try {
                const parsed = JSON.parse(generatedGoal);
                if (parsed.title && parsed.description) {
                  resolve(parsed);
                } else {
                  resolve({ description: generatedGoal });
                }
              } catch {
                resolve({ description: generatedGoal });
              }
            } else {
              resolve(generatedGoal);
            }
          }
        },
      }).catch(error => {
        // Restore original methods
        this.updateGeneratedContent = originalUpdate;
        this.getTranscript = originalGetTranscript;
        reject(error);
      });
    });
  }

  public async generateMultipleAudiences(count: number = 4): Promise<
    {
      title: string;
      description: string;
    }[]
  > {
    // Create instructions for generating multiple diverse audiences
    const instructions = `Generate ${count} distinct target audience segments for the organization.

Return a JSON array where each audience has:
- "title": A concise, descriptive title (2-5 words)
- "description": A detailed 2-3 paragraph description

Make each audience distinct and complementary, covering different market segments, demographics, or needs.

Example format:
[
  {
    "title": "Young Professionals",
    "description": "Detailed description..."
  },
  {
    "title": "Established Families",
    "description": "Detailed description..."
  }
]`;

    // Create a promise to collect the streamed response
    return new Promise((resolve, reject) => {
      let generatedResponse = '';

      // Override the updateGeneratedContent method temporarily to capture the response
      const originalUpdate = this.updateGeneratedContent.bind(this);
      this.updateGeneratedContent = (
        key: SupportedGeneratedContentTypes,
        response: string = '',
      ) => {
        if (key === 'Content Writer | Generate Audience') {
          generatedResponse = response;
        }
        originalUpdate(key, response);
      };

      // Get brand kit data
      const brandKitData = this.getBrandKitData();

      // Build context from brand kit
      let brandContext = '';
      if (brandKitData) {
        brandContext = `\n\nCompany Context:\n`;
        if (brandKitData.brandVoice) {
          brandContext += `Brand Voice: ${brandKitData.brandVoice}\n`;
        }
        if (brandKitData.organizationWebsite) {
          brandContext += `Website: ${brandKitData.organizationWebsite}\n`;
        }
      }

      // Create a mock transcript that contains the brand context
      const brandKitTranscript =
        brandContext || 'No brand kit information available.';

      // Override the getTranscript method temporarily to return brand kit data
      const originalGetTranscript = this.getTranscript.bind(this);
      this.getTranscript = () => brandKitTranscript;

      // Use the existing regenerateStreamResponse method
      this.regenerateStreamResponse({
        key: 'Content Writer | Generate Audience',
        type: 'new',
        customInstructions: instructions,
        useSearch: true,
        skipStoryValidation: true,
        setLoading: (loading: boolean) => {
          if (!loading && generatedResponse) {
            // Restore original methods
            this.updateGeneratedContent = originalUpdate;
            this.getTranscript = originalGetTranscript;

            try {
              const audiences = JSON.parse(generatedResponse);
              if (Array.isArray(audiences)) {
                resolve(audiences);
              } else {
                resolve([]);
              }
            } catch {
              // Try to extract from formatted text if JSON parsing fails
              resolve([]);
            }
          }
        },
      }).catch(error => {
        // Restore original methods
        this.updateGeneratedContent = originalUpdate;
        this.getTranscript = originalGetTranscript;
        reject(error);
      });
    });
  }

  public async generateMultipleGoals(count: number = 4): Promise<
    {
      title: string;
      description: string;
    }[]
  > {
    // Create instructions for generating multiple diverse goals
    const instructions = `Generate ${count} distinct business goals for the organization.

Return a JSON array where each goal has:
- "title": A concise, descriptive title (2-5 words)
- "description": A detailed 2-3 paragraph description with strategies and metrics

Make each goal distinct and strategic, covering different aspects of business growth and success.

Example format:
[
  {
    "title": "Digital Transformation",
    "description": "Detailed description with strategies..."
  },
  {
    "title": "Market Expansion",
    "description": "Detailed description with strategies..."
  }
]`;

    // Create a promise to collect the streamed response
    return new Promise((resolve, reject) => {
      let generatedResponse = '';

      // Override the updateGeneratedContent method temporarily to capture the response
      const originalUpdate = this.updateGeneratedContent.bind(this);
      this.updateGeneratedContent = (
        key: SupportedGeneratedContentTypes,
        response: string = '',
      ) => {
        if (key === 'Content Writer | Generate Goal') {
          generatedResponse = response;
        }
        originalUpdate(key, response);
      };

      // Get brand kit data
      const brandKitData = this.getBrandKitData();

      // Build context from brand kit
      let brandContext = '';
      if (brandKitData) {
        brandContext = `\n\nCompany Context:\n`;
        if (brandKitData.brandVoice) {
          brandContext += `Brand Voice: ${brandKitData.brandVoice}\n`;
        }
        if (brandKitData.organizationWebsite) {
          brandContext += `Website: ${brandKitData.organizationWebsite}\n`;
        }
      }

      // Create a mock transcript that contains the brand context
      const brandKitTranscript =
        brandContext || 'No brand kit information available.';

      // Override the getTranscript method temporarily to return brand kit data
      const originalGetTranscript = this.getTranscript.bind(this);
      this.getTranscript = () => brandKitTranscript;

      // Use the existing regenerateStreamResponse method
      this.regenerateStreamResponse({
        key: 'Content Writer | Generate Goal',
        type: 'new',
        customInstructions: instructions,
        useSearch: true,
        skipStoryValidation: true,
        setLoading: (loading: boolean) => {
          if (!loading && generatedResponse) {
            // Restore original methods
            this.updateGeneratedContent = originalUpdate;
            this.getTranscript = originalGetTranscript;

            try {
              const goals = JSON.parse(generatedResponse);
              if (Array.isArray(goals)) {
                resolve(goals);
              } else {
                resolve([]);
              }
            } catch {
              // Try to extract from formatted text if JSON parsing fails
              resolve([]);
            }
          }
        },
      }).catch(error => {
        // Restore original methods
        this.updateGeneratedContent = originalUpdate;
        this.getTranscript = originalGetTranscript;
        reject(error);
      });
    });
  }

  public async identifySpeakersWithFrameAnalysis({
    selectedSpeakers,
    frameAnalysisResults,
  }: {
    selectedSpeakers?: Speaker[];
    frameAnalysisResults?: FrameAnalysisResult[];
  }): Promise<
    {
      speakerLabel: string;
      name: string;
      title: string;
    }[]
  > {
    // Build frame analysis context
    this.logger.info(
      'Frame analysis results for context:',
      frameAnalysisResults,
    );
    const frameAnalysisContext = frameAnalysisResults
      ?.filter(r => r.hasLowerThird && r.confidence !== 'low')
      .map(r => {
        // Convert spk_0 to Speaker 1 format for the prompt
        const speakerNumber = parseInt(r.speakerLabel.replace('spk_', '')) + 1;
        const parts = [`Speaker ${speakerNumber}`];
        if (r.name) parts.push(`Name: ${r.name}`);
        if (r.title) parts.push(`Title: ${r.title}`);
        if (r.company) parts.push(`Company: ${r.company}`);
        return parts.join(' - ');
      })
      .join('\n');

    this.logger.info('Frame analysis context built:', frameAnalysisContext);

    // Add frame analysis as additional context
    const additionalContext = frameAnalysisContext
      ? `\n\nVisual identification from video frames:\n${frameAnalysisContext}\n\nIMPORTANT: Use this visual information to identify the speakers. Match the Speaker numbers from the visual identification to the corresponding speakers in the transcript.`
      : '';

    // Call the improved chunked method with additional context
    return this.identifySpeakers({
      selectedSpeakers,
      additionalContext,
    });
  }

  public async identifySpeakers({
    selectedSpeakers,
    additionalContext = '',
  }: {
    selectedSpeakers?: Speaker[];
    additionalContext?: string;
  }): Promise<
    {
      speakerLabel: string;
      name: string;
      title: string;
    }[]
  > {
    // Get the transcript text
    const fullTranscript = this.getTranscript(
      'Content Writer | Identify Speaker Names' as SupportedGeneratedContentTypes,
      selectedSpeakers,
    );

    if (!fullTranscript || fullTranscript.length === 0) {
      this.logger.error('No transcript available for speaker identification');
      return [];
    }

    // Approximate token calculation: 1 token ~ 4 characters
    const TOKEN_LIMIT = 1_000_000;
    const AVG_CHARS_PER_TOKEN = 4;
    const SAFETY_MARGIN = 0.9; // 90% of max
    const MAX_CHARS = TOKEN_LIMIT * AVG_CHARS_PER_TOKEN * SAFETY_MARGIN;

    if (fullTranscript.length <= MAX_CHARS) {
      // For shorter transcripts, use the original single-pass approach
      this.logger.info(
        `Processing short transcript (${fullTranscript.length} chars) in single pass...`,
      );
      return this.identifySpeakersInChunk(
        fullTranscript,
        selectedSpeakers,
        additionalContext,
      );
    }

    // VERY UNRELIABLE, MODEL DOESN'T SEEM TO BENEFIT FROM SMALLER CONTEXT

    // Check if transcript is long enough to require chunking
    const TARGET_CHUNK_SIZE = 8000; // Target characters per chunk (approx 2000 tokens)
    const MAX_CHUNKS = 20; // Safety limit to prevent infinite loops

    this.logger.info(
      `Processing long transcript (${fullTranscript.length} chars) in chunks...`,
    );

    const speakerSegments = extractSpeakerSegments(fullTranscript);
    if (speakerSegments.length === 0) {
      return this.identifySpeakersInChunk(
        fullTranscript,
        selectedSpeakers,
        additionalContext,
      );
    }

    const BASE_OCCURRENCE_WEIGHT = 1;
    const ANCHOR_SEED_WEIGHT = 3;
    const EARLY_CHUNK_BOOST = 1.5; // applied to first third of chunks
    const VISUAL_CONTEXT_BOOST = 1.25;
    const hasVisualContext = /Visual identification from video frames:/i.test(
      additionalContext,
    );

    // Phase 1 - Seed Roster / Anchor Establishment
    const anchorMap = hasVisualContext
      ? new Map()
      : await this.seedSpeakerAnchors({
          fullTranscript,
          speakerSegments,
          selectedSpeakers,
          additionalContext,
          anchorSeedWeight: ANCHOR_SEED_WEIGHT,
        });

    // Build overlapping chunks (extracted method)
    const chunks = buildOverlappingChunks(speakerSegments, {
      targetChunkSize: TARGET_CHUNK_SIZE,
      maxChunks: MAX_CHUNKS,
      speakersPerChunk: 10,
      overlapSpeakers: 1,
      minChunkChars: 100,
      minNonFinalChunkChars: 500,
      createThirdPersonSupplementalChunks: !hasVisualContext,
    });

    // Process each chunk (after seed roster). Start with anchors pre-populated.
    const allIdentifiedSpeakers: Map<
      string,
      {
        name: string;
        title: string;
        confidence: number;
        nameCounts: Record<string, number>;
        titleCounts: Record<string, number>;
      }
    > = new Map(anchorMap);

    for (let i = 0; i < chunks.length; i++) {
      if (chunks[i].length >= TARGET_CHUNK_SIZE * 2) {
        this.logger.warn(
          `Chunk ${i + 1} is very large (${
            chunks[i].length
          } chars), which may cause issues.`,
        );
      } else {
        this.logger.info(`Processing chunk ${i + 1}/${chunks.length}...`);
      }

      try {
        const chunkResults = await this.identifySpeakersInChunk(
          chunks[i],
          selectedSpeakers,
          additionalContext +
            `\n\nNote: This is chunk ${i + 1} of ${
              chunks.length
            } from a longer transcript.`,
        );

        const earlyChunkThreshold = Math.ceil(chunks.length / 3);
        const isEarlyChunk = i < earlyChunkThreshold;
        const chunkOccurrenceWeight =
          BASE_OCCURRENCE_WEIGHT * (isEarlyChunk ? EARLY_CHUNK_BOOST : 1);
        const visualMultiplier = hasVisualContext ? VISUAL_CONTEXT_BOOST : 1;

        this.mergeChunkResults({
          chunkResults,
          allIdentifiedSpeakers,
          chunkOccurrenceWeight,
          visualMultiplier,
        });
      } catch (error) {
        this.logger.error(`Error processing chunk ${i + 1}:`, error);
        // Continue with other chunks even if one fails
      }
    }

    const { finalResults, confidenceSummary } = this.finalizeSpeakerResults(
      allIdentifiedSpeakers,
    );
    this.logger.info(
      `Final speaker identification: ${finalResults.length} speakers | confidences ${confidenceSummary}`,
    );
    return finalResults;
  }

  private async identifySpeakersInChunk(
    transcriptChunk: string,
    selectedSpeakers?: Speaker[],
    additionalContext: string = '',
  ): Promise<{ speakerLabel: string; name: string; title: string }[]> {
    // Create a promise to collect the streamed response
    return new Promise((resolve, reject) => {
      let generatedResponse = '';

      // Override methods temporarily
      const originalUpdate = this.updateGeneratedContent.bind(this);
      const originalGetTranscript = this.getTranscript.bind(this);

      this.updateGeneratedContent = (
        key: SupportedGeneratedContentTypes,
        response: string = '',
      ) => {
        if (key === 'Content Writer | Identify Speaker Names') {
          generatedResponse = response;
        }
        originalUpdate(key, response);
      };

      // Override getTranscript to return the chunk
      this.getTranscript = () => transcriptChunk;

      // Use the existing regenerateStreamResponse method
      this.regenerateStreamResponse({
        key: 'Content Writer | Identify Speaker Names' as SupportedGeneratedContentTypes,
        type: 'new',
        customInstructions: additionalContext,
        useSearch: false,
        responseFormat: 'speaker_id',
        useBetterReasoning: true,
        selectedSpeakers, // Pass speakers for formatting
        setLoading: (loading: boolean) => {
          if (!loading && generatedResponse) {
            // Restore original methods
            this.updateGeneratedContent = originalUpdate;
            this.getTranscript = originalGetTranscript;

            try {
              // Clean the response before parsing
              let cleanedResponse = generatedResponse.trim();

              // Remove any markdown code blocks if present
              cleanedResponse = cleanedResponse.replace(
                /^```(?:json)?\s*/i,
                '',
              );
              cleanedResponse = cleanedResponse.replace(/\s*```$/i, '');

              // Remove any leading/trailing whitespace again
              cleanedResponse = cleanedResponse.trim();

              // Try to extract JSON from the response
              let identifiedSpeakers;
              try {
                identifiedSpeakers = JSON.parse(cleanedResponse);
              } catch (e) {
                // If direct parsing fails, try to find JSON array in the response
                const jsonMatch = cleanedResponse.match(
                  /\[\s*\{[\s\S]*?\}\s*\]/,
                );
                if (jsonMatch) {
                  identifiedSpeakers = JSON.parse(jsonMatch[0]);
                } else {
                  // As a last resort, check if the response got truncated
                  if (
                    cleanedResponse.includes('[') &&
                    !cleanedResponse.includes(']')
                  ) {
                    this.logger.error('Response appears to be truncated');
                    throw new Error('Response was truncated - incomplete JSON');
                  }
                  throw new Error('No valid JSON array found in response');
                }
              }

              if (
                !Array.isArray(identifiedSpeakers) &&
                identifiedSpeakers?.speakers
              ) {
                identifiedSpeakers = identifiedSpeakers.speakers;
              }

              // Validate the response structure
              // find number in speakerLabel
              if (Array.isArray(identifiedSpeakers)) {
                const validSpeakers = identifiedSpeakers
                  .filter(
                    speaker =>
                      speaker.speakerLabel &&
                      speaker.name &&
                      typeof speaker.speakerLabel === 'string' &&
                      typeof speaker.name === 'string' &&
                      (speaker.title === undefined ||
                        speaker.title === null ||
                        typeof speaker.title === 'string') &&
                      speaker.speakerLabel.replace(/[^0-9]/g, '').length > 0,
                  )
                  .map(speaker => ({
                    ...speaker,
                    title: speaker.title || '',
                    speakerLabel: `spk_${
                      Number(speaker.speakerLabel.replace(/[^0-9]/g, '')) - 1
                    }`,
                  }));

                resolve(validSpeakers);
              } else {
                this.logger.error(
                  'Response is not an array:',
                  identifiedSpeakers,
                );
                resolve([]);
              }
            } catch (error) {
              this.logger.error(
                'Error parsing speaker identification response:',
                error,
              );
              this.logger.error('Raw response:', generatedResponse);

              // Try to parse the text format as a fallback
              try {
                const speakers =
                  this.parseTextFormatSpeakers(generatedResponse);
                resolve(speakers);
              } catch (fallbackError) {
                this.logger.error(
                  'Fallback parsing also failed:',
                  fallbackError,
                );
                resolve([]);
              }
            }
          }
        },
      }).catch(error => {
        // Restore original methods
        this.updateGeneratedContent = originalUpdate;
        this.getTranscript = originalGetTranscript;
        this.logger.error('Error identifying speakers:', error);
        resolve([]); // Return empty array on error rather than rejecting
      });
    });
  }

  // Helper method to parse text format responses
  private parseTextFormatSpeakers(text: string): {
    speakerLabel: string;
    name: string;
    title: string;
  }[] {
    const speakers: any[] = [];

    // Split by numbered items
    const lines = text.split('\n');
    let currentSpeaker: any = null;
    let speakerIndex = 0;

    for (const line of lines) {
      const trimmedLine = line.trim();

      // Check for numbered speaker entries (e.g., "1. **Name**" or "1. Name")
      const nameMatch = trimmedLine.match(/^\d+\.\s+\*{0,2}(.+?)\*{0,2}$/);
      if (nameMatch) {
        if (currentSpeaker && currentSpeaker.name) {
          speakers.push(currentSpeaker);
        }
        currentSpeaker = {
          speakerLabel: `spk_${speakerIndex}`,
          name: nameMatch[1].trim(),
          title: '',
        };
        speakerIndex++;
      }

      // Check for title lines (e.g., "- **Title:** Company | Position")
      const titleMatch = trimmedLine.match(
        /[-•]\s+\*{0,2}Title:\*{0,2}\s+(.+)$/i,
      );
      if (titleMatch && currentSpeaker) {
        currentSpeaker.title = titleMatch[1].trim();
      }
    }

    // Add the last speaker if exists
    if (currentSpeaker && currentSpeaker.name) {
      speakers.push(currentSpeaker);
    }

    return speakers.filter(s => s.name);
  }

  /**
   * Seed speaker anchors by constructing a diversity-first roster slice of the transcript
   * prioritizing: self-introductions > third-person introductions > chronological order.
   * Each unique speaker contributes a truncated snippet to a roster chunk which is used
   * for an initial identification pass. Results are converted into an anchor map with
   * elevated confidence/weight (ANCHOR_SEED_WEIGHT) to stabilize subsequent chunk merging.
   */
  private async seedSpeakerAnchors(params: {
    fullTranscript: string;
    speakerSegments: { speaker: string; content: string }[];
    selectedSpeakers?: Speaker[];
    additionalContext: string;
    anchorSeedWeight: number;
  }): Promise<
    Map<
      string,
      {
        name: string;
        title: string;
        confidence: number;
        nameCounts: Record<string, number>;
        titleCounts: Record<string, number>;
      }
    >
  > {
    const {
      fullTranscript,
      speakerSegments,
      selectedSpeakers,
      additionalContext,
      anchorSeedWeight,
    } = params;
    const rosterText = buildRosterTextForAnchors(
      speakerSegments,
      fullTranscript,
    );
    const anchorMap: Map<
      string,
      {
        name: string;
        title: string;
        confidence: number;
        nameCounts: Record<string, number>;
        titleCounts: Record<string, number>;
      }
    > = new Map();
    try {
      const anchorResults = await this.identifySpeakersInChunk(
        rosterText,
        selectedSpeakers,
        additionalContext +
          '\n\nThis is an initial seed roster chunk. Produce provisional mappings. If uncertain use placeholder like "Unknown".',
      );
      anchorResults.forEach(r => {
        anchorMap.set(r.speakerLabel, {
          name: r.name,
          title: r.title,
          confidence: anchorSeedWeight,
          nameCounts: r.name
            ? { [this.normalizeName(r.name)]: anchorSeedWeight }
            : {},
          titleCounts: r.title
            ? { [this.normalizeName(r.title)]: anchorSeedWeight }
            : {},
        });
      });
      this.logger.info(
        `Anchors established: ${anchorMap.size} provisional speaker identities`,
      );
    } catch (e) {
      this.logger.warn(
        'Seed roster identification failed; proceeding without anchors',
        e,
      );
    }
    return anchorMap;
  }

  /**
   * Merge chunk-level identification results into the aggregate map with
   * weighting & inline upgrade heuristics.
   */
  private mergeChunkResults(params: {
    chunkResults: { speakerLabel: string; name: string; title: string }[];
    allIdentifiedSpeakers: Map<
      string,
      {
        name: string;
        title: string;
        confidence: number;
        nameCounts: Record<string, number>;
        titleCounts: Record<string, number>;
      }
    >;
    chunkOccurrenceWeight: number;
    visualMultiplier: number;
  }) {
    const {
      chunkResults,
      allIdentifiedSpeakers,
      chunkOccurrenceWeight,
      visualMultiplier,
    } = params;

    chunkResults.forEach(speaker => {
      const existing = allIdentifiedSpeakers.get(speaker.speakerLabel);
      if (!existing) {
        allIdentifiedSpeakers.set(speaker.speakerLabel, {
          name: speaker.name,
          title: speaker.title,
          confidence: chunkOccurrenceWeight * visualMultiplier,
          nameCounts: speaker.name
            ? {
                [this.normalizeName(speaker.name)]:
                  chunkOccurrenceWeight * visualMultiplier,
              }
            : {},
          titleCounts: speaker.title
            ? {
                [this.normalizeName(speaker.title)]:
                  chunkOccurrenceWeight * visualMultiplier,
              }
            : {},
        });
        return;
      }
      // Existing speaker label encountered again
      const weighted = chunkOccurrenceWeight * visualMultiplier;
      existing.confidence += weighted;
      const normalizedIncomingName = this.normalizeName(speaker.name || '');
      if (normalizedIncomingName) {
        existing.nameCounts[normalizedIncomingName] =
          (existing.nameCounts[normalizedIncomingName] || 0) + weighted;
        if (this.shouldReplaceInline(existing.name, normalizedIncomingName)) {
          existing.name = normalizedIncomingName;
        }
      }
      const normalizedIncomingTitle = this.normalizeName(speaker.title || '');
      if (normalizedIncomingTitle) {
        existing.titleCounts[normalizedIncomingTitle] =
          (existing.titleCounts[normalizedIncomingTitle] || 0) + weighted;
        // Inline replace only if existing title empty or candidate extends (adds tokens)
        if (
          !existing.title ||
          this.isExtension(existing.title, normalizedIncomingTitle)
        ) {
          existing.title = normalizedIncomingTitle;
        }
      }
    });
  }

  /**
   * Finalize canonical name/title selection and build confidence summary from the aggregate map.
   */
  private finalizeSpeakerResults(
    allIdentifiedSpeakers: Map<
      string,
      {
        name: string;
        title: string;
        confidence: number;
        nameCounts: Record<string, number>;
        titleCounts: Record<string, number>;
      }
    >,
  ): {
    finalResults: { speakerLabel: string; name: string; title: string }[];
    confidenceSummary: string;
  } {
    const finalResults = Array.from(allIdentifiedSpeakers.entries()).map(
      ([speakerLabel, data]) => {
        const canonicalName = this.selectCanonical(
          data.nameCounts,
          this.normalizeName(data.name),
        );
        const canonicalTitle = this.selectCanonical(
          data.titleCounts,
          this.normalizeName(data.title),
        );
        return { speakerLabel, name: canonicalName, title: canonicalTitle };
      },
    );
    const confidenceSummary = Array.from(allIdentifiedSpeakers.entries())
      .map(([label, d]) => `${label}:${d.confidence.toFixed(1)}`)
      .join(' ');
    return { finalResults, confidenceSummary };
  }

  // ---------------------------------------------------------------------------
  // Identity Heuristic Helper Methods (extracted from identifySpeakers)
  // ---------------------------------------------------------------------------
  /** Normalize a name/title string for stable comparison & counting. */
  private normalizeName(raw: string | undefined | null): string {
    if (!raw) return '';
    let s = raw
      .replace(/^["'`\-\s]+|["'`\-\s]+$/g, '') // trim quotes / dashes
      .replace(/\s+/g, ' ') // collapse whitespace
      .trim();
    // Guard against pathological length (avoid huge hallucinations skewing counts)
    if (s.length > 120) s = s.slice(0, 120);
    return s;
  }

  /** Generic/placeholder surface forms that shouldn't override a specific identity. */
  private isGenericName(name: string): boolean {
    const n = name.toLowerCase();
    if (!n) return true;
    return (
      n === 'unknown' ||
      n === 'speaker' ||
      /^speaker \d+$/.test(n) ||
      /^spk[_-]?\d+$/.test(n) ||
      ['host', 'moderator', 'guest', 'narrator', 'announcer'].includes(n)
    );
  }

  /** Tokenize a surface (letters + numbers) for extension checks & specificity scoring. */
  private tokenize(str: string): string[] {
    if (!str) return [];
    return str
      .split(/[^A-Za-z0-9]+/)
      .map(t => t.trim())
      .filter(Boolean);
  }

  /**
   * Returns true if candidate is a simple extension of existing (e.g. "Jane" -> "Jane Doe").
   * All existing tokens must appear in order at the start of candidate and candidate has > tokens.
   */
  private isExtension(existing: string, candidate: string): boolean {
    const a = this.tokenize(this.normalizeName(existing).toLowerCase());
    const b = this.tokenize(this.normalizeName(candidate).toLowerCase());
    if (a.length === 0 || b.length === 0) return false;
    if (b.length <= a.length) return false;
    return a.every((tok, idx) => b[idx] === tok);
  }

  /** Decide whether to inline replace an existing name with an incoming candidate during streaming merge. */
  private shouldReplaceInline(existing: string, incoming: string): boolean {
    if (!existing) return !!incoming;
    if (!incoming) return false;
    const normalizedExisting = this.normalizeName(existing);
    const normalizedIncoming = this.normalizeName(incoming);
    if (!normalizedIncoming) return false;
    if (normalizedExisting === normalizedIncoming) return false;
    const existingGeneric = this.isGenericName(normalizedExisting);
    const incomingGeneric = this.isGenericName(normalizedIncoming);
    // Upgrade from generic -> specific
    if (existingGeneric && !incomingGeneric) return true;
    // Never downgrade specific -> generic
    if (!existingGeneric && incomingGeneric) return false;
    // Allow simple extensions (adds surname etc.)
    if (this.isExtension(normalizedExisting, normalizedIncoming)) return true;
    return false;
  }

  /**
   * Select a canonical surface from weighted occurrence counts.
   * Preference order: highest weight (non-generic) -> max token count -> longest length.
   */
  private selectCanonical(
    counts: Record<string, number>,
    current?: string,
  ): string {
    const entries = Object.entries(counts);
    if (entries.length === 0) return current || '';
    // Separate generic vs specific to prioritize specific forms.
    const scored = entries.map(([name, weight]) => {
      const norm = this.normalizeName(name);
      const tokens = this.tokenize(norm);
      return {
        name: norm,
        weight,
        tokens,
        tokenCount: tokens.length,
        isGeneric: this.isGenericName(norm),
        length: norm.length,
      };
    });
    scored.sort((a, b) => {
      // Non-generic before generic
      if (a.isGeneric !== b.isGeneric) return a.isGeneric ? 1 : -1;
      // Higher weight
      if (b.weight !== a.weight) return b.weight - a.weight;
      // More tokens (greater specificity)
      if (b.tokenCount !== a.tokenCount) return b.tokenCount - a.tokenCount;
      // Longer length as final tiebreaker
      if (b.length !== a.length) return b.length - a.length;
      return a.name.localeCompare(b.name);
    });
    return scored[0].name;
  }
}
